import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import type { PropertyFileResponse, PropertyFileTreeNode } from '@/shared/api';
import { collectFolderNodes } from './akteBrowserNav';
import { bulkCategory } from './akteBulkRules';
import { isCategoryMoveTarget } from './akteFolderRules';
import { folderDisplayName, folderDisplayPathWithinCategory } from './akteFolderNames';
import { FolderTargetPicker } from './FolderTargetPicker';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  files: PropertyFileResponse[];
  tree: PropertyFileTreeNode;
  onMove: (targetFolder: string) => void;
  onCreateSubfolder: () => void;
}

/** Bulk-„Verschieben nach…": Ziel-Ordner-Picker für eine Auswahl gleicher Top-Kategorie (§4.6). */
export function BulkMoveDialog({
  open,
  onOpenChange,
  files,
  tree,
  onMove,
  onCreateSubfolder,
}: Props) {
  const [target, setTarget] = useState<string | null>(null);
  const category = bulkCategory(files);

  const targets = useMemo(
    () =>
      category === null
        ? []
        : collectFolderNodes(tree)
            .filter((n) => isCategoryMoveTarget(category, n.path))
            .map((n) => ({ path: n.path, label: folderDisplayPathWithinCategory(n.path) })),
    [tree, category],
  );

  function confirm() {
    if (target) {
      onMove(target);
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{files.length} Dateien verschieben…</DialogTitle>
          <DialogDescription>
            Nur Ordner in{' '}
            <strong>{category ? folderDisplayName(category) : 'dieser Kategorie'}</strong> — die
            Kategorie bleibt gleich.
          </DialogDescription>
        </DialogHeader>
        <FolderTargetPicker
          targets={targets}
          selected={target}
          onSelect={setTarget}
          onCreateSubfolder={onCreateSubfolder}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button variant="brand" disabled={!target} onClick={confirm}>
            Verschieben
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
